/**
 * sessionActions — mirrors the SessionStatus PHP enum's can* methods.
 *
 * Single source of truth for which actions are valid for a given session status.
 * The UI only renders actions listed here for the session's current status.
 *
 * SessionStatus values (from app/Enums/SessionStatus.php):
 *   scheduled           → canStart, canCancel
 *   in_progress         → canEnd
 *   awaiting_report     → canComplete (report form)
 *   completed           → (none)
 *   cancelled_by_parent → (none)
 *   cancelled_by_tutor  → (none)
 */

/**
 * @typedef {'start'|'end'|'report'|'cancel'} SessionAction
 */

/**
 * Returns the list of allowed actions for a session status.
 *
 * @param {string} status - raw API status value
 * @returns {SessionAction[]}
 */
export function getAllowedActions(status) {
  switch (status) {
    case 'scheduled':
      return ['start', 'cancel'];
    case 'in_progress':
      return ['end'];
    case 'awaiting_report':
      return ['report'];
    case 'completed':
    case 'cancelled_by_parent':
    case 'cancelled_by_tutor':
      return [];
    default:
      return [];
  }
}

/**
 * @param {string} status
 * @returns {boolean}
 */
export function canStart(status) {
  return status === 'scheduled';
}

/**
 * @param {string} status
 * @returns {boolean}
 */
export function canEnd(status) {
  return status === 'in_progress';
}

/**
 * canComplete mirrors the server-side canComplete (awaiting_report → completed via report).
 * @param {string} status
 * @returns {boolean}
 */
export function canComplete(status) {
  return status === 'awaiting_report';
}

/**
 * @param {string} status
 * @returns {boolean}
 */
export function canCancel(status) {
  return status === 'scheduled';
}

/**
 * @param {string} status
 * @returns {boolean}
 */
export function isCancelled(status) {
  return status === 'cancelled_by_parent' || status === 'cancelled_by_tutor';
}
